import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Play } from 'lucide-react';
import { taskEnqueue, pollTaskStatus } from '@/utils/taskHelpers';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/hooks/useAppSelector';
import { TaskType, isValidTaskType } from '@/types/taskTypes';

interface TaskButtonProps {
  taskType: TaskType | string;
  subslug?: string;
  version?: string;  // Optional specific version to use (overrides Redux version)
  icon?: React.ReactNode;
  buttonText: string;
  onTaskComplete?: (result: any) => void;
  className?: string;
}

/**
 * A button component that creates and monitors a task
 *
 * @param taskType - The type of task to create (use TaskType enum)
 * @param subslug - Optional subslug to append to the company slug
 * @param version - Optional specific version to use (overrides Redux version)
 * @param icon - Optional custom icon to display on the button
 * @param buttonText - Text to display on the button
 * @param onTaskComplete - Optional callback when task completes
 * @param className - Optional additional CSS classes
 */
export const TaskButton: React.FC<TaskButtonProps> = ({
  taskType,
  subslug,
  version,
  icon = <Play className="h-4 w-4 mr-2" />,
  buttonText,
  onTaskComplete,
  className = '',
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const { toast } = useToast();

  // Get company slug and selected version from Redux store
  const company = useAppSelector((state) => state.company.company);
  const selectedVersion = useAppSelector((state) => state.versions.selectedVersion);

  // Handle task status changes
  const handleStatusChange = (status: string, result: any) => {
    // Update the task status
    setTaskStatus(status);

    // Keep button disabled for pending, processing, and running states
    if (status !== 'pending' && status !== 'processing' && status !== 'running') {
      setIsRunning(false);
      setTaskStatus(null);

      // Show appropriate toast based on status
      if (status === 'done') {
        toast({
          title: 'Task Completed',
          description: `${taskType} task completed successfully`,
        });

        // Call the onTaskComplete callback if provided
        if (onTaskComplete) {
          onTaskComplete(result);
        }
      } else if (status === 'error' || status === 'failed') {
        toast({
          variant: 'destructive',
          title: 'Task Failed',
          description: result?.message || `${taskType} task failed`,
        });
      }
    }
  };

  // Start the task
  const startTask = async () => {
    if (!company?.slug) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No company selected',
      });
      return;
    }

    // Validate task type
    if (typeof taskType === 'string' && !isValidTaskType(taskType)) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Invalid task type: ${taskType}`,
      });
      return;
    }

    // Combine company slug with subslug if provided
    const fullSlug = subslug ? `${company.slug}${subslug}` : company.slug;

    setIsRunning(true);

    try {
      // Prepare task options
      const options: Record<string, any> = {};

      // Add version parameter for all task types except SCREENSHOT_PAGE
      if (taskType !== TaskType.SCREENSHOT_PAGE) {
        // Use the version prop if provided, otherwise use the Redux selected version
        const effectiveVersion = version || selectedVersion;
        if (effectiveVersion) {
          options.version = effectiveVersion;
        }
      } else {
        options.force = true;
        options.useAI = true;
      }

      // Enqueue the task with appropriate options
      const data = await taskEnqueue(taskType, fullSlug, options);

      if (data?.task_id) {
        setTaskId(data.task_id);

        // Start polling for task status
        pollTaskStatus(data.task_id, handleStatusChange);

        toast({
          title: 'Task Started',
          description: `${taskType} task started for ${fullSlug}`,
        });
      } else {
        throw new Error('No task ID returned');
      }
    } catch (error: any) {
      console.error('Error starting task:', error);
      setIsRunning(false);

      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || `Failed to start ${taskType} task`,
      });
    }
  };

  // Clean up any polling when component unmounts
  useEffect(() => {
    return () => {
      // No explicit cleanup needed as pollTaskStatus will stop on its own
      // when max attempts are reached or task completes
    };
  }, []);

  // Get the appropriate button text based on task status
  const getButtonText = () => {
    if (!isRunning) return buttonText;

    switch (taskStatus) {
      case 'pending':
        return 'Pending...';
      case 'processing':
        return 'Processing...';
      case 'running':
        return 'Running...';
      default:
        return 'Working...';
    }
  };

  return (
    <Button
      onClick={startTask}
      disabled={isRunning || !company?.slug}
      className={className}
      size="sm"
    >
      {isRunning ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        icon
      )}
      {getButtonText()}
    </Button>
  );
};

export default TaskButton;
